import { useState, useEffect } from "react";
import axios from "axios";
import AppShell from "../components/layout/AppShell";
import AppFooter from "../components/layout/AppFooter";
import TopNav from "../components/layout/TopNav";

const API = process.env.REACT_APP_API_URL || "http://localhost:3001";

function StatCard({ icon, label, value, sub, color }) {
    return (
        <div className="section-card" style={{ marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div>
                    <div className="muted" style={{ fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.03em", color: color || "rgb(var(--text))" }}>{value}</div>
                    {sub && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{sub}</div>}
                </div>
                <div style={{
                    width: 40, height: 40, borderRadius: 12,
                    background: "rgba(110,86,207,0.12)",
                    border: "1px solid rgba(110,86,207,0.2)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 20
                }}>{icon}</div>
            </div>
        </div>
    );
}

function MiniBar({ label, value, max, color }) {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    return (
        <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 13 }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{value} <span className="muted">({pct}%)</span></span>
            </div>
            <div className="tech-bar">
                <div className="tech-bar__fill" style={{ width: `${pct}%`, background: color || "rgba(110,86,207,0.75)" }} />
            </div>
        </div>
    );
}

export default function AdminDashboard({ onLogout, onNavigate }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [surveyData, setSurveyData] = useState([]);
    const [surveyLoading, setSurveyLoading] = useState(false);
    const [kycData, setKycData] = useState([]);
    const [kycLoading, setKycLoading] = useState(false);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API}/admin/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(res.data);
            setLastUpdate(new Date());
            setError(null);
        } catch (err) {
            setError(err.response?.status === 403 ? "Accès refusé." : "Erreur de chargement.");
        } finally {
            setLoading(false);
        }
    };

    const fetchSurvey = async () => {
        setSurveyLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API}/survey`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSurveyData(res.data);
        } catch (err) {
            console.error(err);
        }
        setSurveyLoading(false);
    };

    useEffect(() => {
        fetchStats();
        const poll = setInterval(fetchStats, 30000);
        return () => clearInterval(poll);
    }, []);

    const fetchKyc = async () => {
        setKycLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${API}/kyc/pending`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setKycData(res.data);
        } catch (err) {
            console.error(err);
        }
        setKycLoading(false);
    };

    const handleKyc = async (userId, status) => {
        try {
            const token = localStorage.getItem("token");
            await axios.patch(`${API}/kyc/${userId}/validate`, { status }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchKyc();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <AppShell
            header={<TopNav isLoggedIn={true} onLogout={onLogout} onNavigate={onNavigate} />}
            footer={<AppFooter onNavigate={onNavigate} />}
            onNavigate={onNavigate}
        >
            <div style={{ maxWidth: 900, margin: "0 auto" }}>
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                    <h2 className="page-title" style={{ margin: 0 }}>Dashboard</h2>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        {lastUpdate && (
                            <span className="muted" style={{ fontSize: 12 }}>
                                Mis à jour {lastUpdate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                            </span>
                        )}
                        <button className="btn" style={{ fontSize: 12, padding: "6px 12px" }} onClick={fetchStats}>
                            ↻ Actualiser
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                    <button className="btn" onClick={() => setActiveTab('dashboard')}
                        style={{ fontSize: 13, padding: '7px 14px', ...(activeTab === 'dashboard' ? { background: 'rgba(110,86,207,0.18)', borderColor: 'rgba(110,86,207,0.4)', color: '#a78bfa' } : {}) }}>
                        Dashboard
                    </button>
                    <button className="btn" onClick={() => { setActiveTab('survey'); fetchSurvey(); }}
                        style={{ fontSize: 13, padding: '7px 14px', ...(activeTab === 'survey' ? { background: 'rgba(110,86,207,0.18)', borderColor: 'rgba(110,86,207,0.4)', color: '#a78bfa' } : {}) }}>
                        Sondage
                    </button>
                    <button className="btn" onClick={() => { setActiveTab('kyc'); fetchKyc(); }}
                        style={{ fontSize: 13, padding: '7px 14px', ...(activeTab === 'kyc' ? { background: 'rgba(110,86,207,0.18)', borderColor: 'rgba(110,86,207,0.4)', color: '#a78bfa' } : {}) }}>
                        KYC
                    </button>
                </div>

                {/* Loading / Error */}
                {loading && activeTab === 'dashboard' && (
                    <div className="section-card">
                        <p className="muted">Chargement des statistiques…</p>
                    </div>
                )}

                {error && activeTab === 'dashboard' && (
                    <div className="section-card">
                        <p style={{ color: "#f87171" }}>{error}</p>
                    </div>
                )}

                {/* ── Tab: Dashboard ── */}
                {activeTab === 'dashboard' && stats && (
                    <>
                        {/* Tickets stats */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 14 }}>
                            <StatCard icon="🎫" label="Total tickets" value={stats.tickets.total} sub={`+${stats.tickets.aujourdhui} aujourd'hui`} />
                            <StatCard icon="⏳" label="En attente" value={stats.tickets.en_attente} color={stats.tickets.en_attente > 0 ? "#f59e0b" : undefined} />
                            <StatCard icon="✅" label="Résolus" value={stats.tickets.resolus} color="#4ade80" />
                        </div>

                        {/* Users + revenus */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 14 }}>
                            <StatCard icon="👥" label="Clients" value={stats.users.total_clients} />
                            <StatCard icon="🔧" label="Techniciens validés" value={stats.users.techs_valides} sub={`${stats.users.total_technicians} au total`} />
                            <StatCard icon="💶" label="Revenus totaux" value={`${stats.revenus.total.toFixed(2)} €`} sub={`${stats.revenus.aujourdhui.toFixed(2)} € aujourd'hui`} color="#a78bfa" />
                        </div>

                        {/* Répartition tickets */}
                        <div className="section-card" style={{ marginBottom: 14 }}>
                            <div className="section-head">
                                <h3 className="section-title">Répartition des tickets</h3>
                            </div>
                            <MiniBar label="En attente" value={stats.tickets.en_attente} max={stats.tickets.total} color="rgba(245,158,11,0.75)" />
                            <MiniBar label="En cours" value={stats.tickets.en_cours} max={stats.tickets.total} color="rgba(99,102,241,0.75)" />
                            <MiniBar label="Résolus" value={stats.tickets.resolus} max={stats.tickets.total} color="rgba(34,197,94,0.75)" />
                        </div>

                        {/* Techniciens */}
                        <div className="section-card">
                            <div className="section-head">
                                <h3 className="section-title">Techniciens</h3>
                            </div>
                            <MiniBar
                                label="Validés"
                                value={stats.users.techs_valides}
                                max={stats.users.total_technicians}
                                color="rgba(34,197,94,0.75)"
                            />
                            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                                <div className="badge done">
                                    <span className="badge-dot" />
                                    {stats.users.techs_valides} validés
                                </div>
                                <div className="badge waiting">
                                    <span className="badge-dot" />
                                    {stats.users.total_technicians - stats.users.techs_valides} en attente
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* ── Tab: Sondage ── */}
                {activeTab === 'survey' && (
                    <div>
                        {surveyLoading ? (
                            <div className="section-card"><p className="muted">Chargement…</p></div>
                        ) : surveyData.length === 0 ? (
                            <div className="section-card">
                                <div className="empty-state">
                                    <div className="empty-icon">📊</div>
                                    <p className="empty-title">Aucune réponse</p>
                                    <p className="empty-sub">Les réponses au sondage apparaîtront ici.</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                                    <StatCard icon="📊" label="Réponses" value={surveyData.length} />
                                    <StatCard icon="📧" label="Emails collectés" value={surveyData.filter(r => r.email).length} color="#4ade80" />
                                </div>
                                {surveyData.map((response) => (
                                    <div key={response.id} className="section-card" style={{ marginBottom: 14 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                            <span style={{ fontSize: 13, fontWeight: 700 }}>Réponse #{response.id}</span>
                                            <span className="muted" style={{ fontSize: 12 }}>
                                                {new Date(response.submitted_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                        {response.email && (
                                            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', fontSize: 13 }}>
                                                📧 {response.email}
                                            </div>
                                        )}
                                        {Object.entries(response.answers).map(([qId, answer]) => (
                                            <div key={qId} style={{ marginBottom: 10 }}>
                                                <div className="muted" style={{ fontSize: 11, marginBottom: 3 }}>Question {qId}</div>
                                                <div style={{ fontSize: 13 }}>
                                                    {Array.isArray(answer) ? answer.join(', ') : answer}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </>
                        )}

                    </div>
                )}
                {/* ── Tab: KYC ── */}
                {activeTab === 'kyc' && (
                    <div>
                        {kycLoading ? (
                            <div className="section-card"><p className="muted">Chargement…</p></div>
                        ) : kycData.length === 0 ? (
                            <div className="section-card">
                                <div className="empty-state">
                                    <div className="empty-icon">🪪</div>
                                    <p className="empty-title">Aucun KYC en attente</p>
                                    <p className="empty-sub">Les demandes de vérification apparaîtront ici.</p>
                                </div>
                            </div>
                        ) : (
                            (() => {
                                // Grouper par user_id
                                const grouped = {};
                                kycData.forEach(doc => {
                                    if (!grouped[doc.user_id]) {
                                        grouped[doc.user_id] = { email: doc.email, tech_name: doc.tech_name, score: doc.score, docs: [] };
                                    }
                                    grouped[doc.user_id].docs.push(doc);
                                });

                                return Object.entries(grouped).map(([userId, data]) => (
                                    <div key={userId} className="section-card" style={{ marginBottom: 14 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                            <div>
                                                <span style={{ fontSize: 14, fontWeight: 700 }}>{data.tech_name}</span>
                                                <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>{data.email}</span>
                                            </div>
                                            <span className="muted" style={{ fontSize: 12 }}>Score IA : {data.score}/100</span>
                                        </div>

                                        {data.docs.map(doc => (
                                            <div key={doc.id} style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                padding: '10px 12px', marginBottom: 6, borderRadius: 8,
                                                background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 16 }}>
                                                        {doc.type === 'identity' ? '🪪' : doc.type === 'selfie' ? '🤳' : '🏠'}
                                                    </span>
                                                    <div>
                                                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                                                            {doc.type === 'identity' ? "Pièce d'identité" : doc.type === 'selfie' ? 'Selfie' : 'Justificatif de domicile'}
                                                        </div>
                                                        <div className="muted" style={{ fontSize: 11 }}>{doc.filename}</div>
                                                    </div>
                                                </div>
                                                <a
                                                    href={`${API}/kyc/file/${doc.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn"
                                                    style={{ fontSize: 11, padding: '4px 10px' }}
                                                >
                                                    Voir
                                                </a>
                                            </div>
                                        ))
                                        }

                                        < div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                                            <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => handleKyc(userId, 'verified')}>
                                                ✓ Valider
                                            </button>
                                            <button className="btn btn-danger" style={{ fontSize: 13 }} onClick={() => handleKyc(userId, 'rejected')}>
                                                ✕ Refuser
                                            </button>
                                        </div>
                                    </div>
                                ));
                            })()
                        )}
                    </div>
                )
                }

            </div >
        </AppShell >
    );
}